import Api from '@/api'
import File from '@/models/File'

/**
 * Same match rule as mixins/fileDuplicateMatch.js's matchesName: exact name,
 * or name as a prefix followed by a word boundary (so "White Card: expiry
 * 2027" still matches a "White Card" request without "White Cards" doing so).
 */
function filterFilesByRequestName(files, name) {
  const normalizedName = name ? String(name).trim().toLowerCase() : ''
  if (!normalizedName) return files

  return files.filter((f) => {
    const fileName = (f && f.name ? String(f.name) : '').trim().toLowerCase()
    if (fileName === normalizedName) return true
    if (fileName.startsWith(normalizedName)) {
      const next = fileName.charAt(normalizedName.length)
      return next === '' || /[^a-z0-9]/.test(next)
    }
    return false
  })
}

/**
 * Which existing files count as candidates to answer a request with.
 * Prefer matching by template_id - a file created from the same template as
 * the request is the same *type* of document regardless of what the tradie
 * named it (e.g. a "White Card" file renamed "expires next month" would
 * never match on name alone). Falls back to the name rule above for
 * template-less requests (the "New Request" - no template - flow) or a
 * template-less file (freeform uploads never carry a template_id).
 */
function filterFilesForRequest(files, { name, templateId } = {}) {
  if (templateId) {
    // == not === : the API's template_id (int column) isn't guaranteed to
    // arrive JSON-typed as a number rather than a numeric string.
    const byTemplate = files.filter((f) => f && f.template_id == templateId) // eslint-disable-line eqeqeq
    if (byTemplate.length) return byTemplate
  }
  return filterFilesByRequestName(files, name)
}

export default {
  namespaced: true,
  state: {
    files: [],
    filesMeta: {
      current_page: 1,
    },
    sharedFiles: [],
    sharedFilesMeta: {
      current_page: 1,
    },
    sentFiles: [],
    sentFilesMeta: {
      current_page: 1,
    },
    infoTypes: [],
    currentScreen: 'New',
    file: null,
  },

  mutations: {
    setInfoTypes(state, infoTypes) {
      state.infoTypes = infoTypes
    },

    clearInfoTypes(state) {
      state.infoTypes = null
    },

    setCurrentScreen(state, currentScreen) {
      state.currentScreen = currentScreen
    },

    clearCurrentScreen(state) {
      state.currentScreen = null
    },

    setFiles(state, files) {
      state.files = files
    },

    setFilesMeta(state, meta) {
      state.filesMeta = meta
    },

    clearFiles(state) {
      state.files = []
    },

    clearFilesMeta(state) {
      state.filesMeta = {
        current_page: 1,
      }
    },

    setSharedFiles(state, sharedFiles) {
      state.sharedFiles = sharedFiles
    },

    setSharedFilesMeta(state, meta) {
      state.sharedFilesMeta = meta
    },

    clearSharedFiles(state) {
      state.sharedFiles = []
    },

    clearSharedFilesMeta(state) {
      state.sharedFilesMeta = {
        current_page: 1,
      }
    },

    setSentFiles(state, sentFiles) {
      state.sentFiles = sentFiles
    },

    setSentFilesMeta(state, meta) {
      state.sentFilesMeta = meta
    },

    clearSentFiles(state) {
      state.sentFiles = []
    },

    clearSentFilesMeta(state) {
      state.sentFilesMeta = {
        current_page: 1,
      }
    },

    setFile(state, file) {
      if (file instanceof File) {
        state.file = file
      } else {
        state.file = new File(file)
      }
    },

    clearFile(state) {
      state.file = null
    },

    mergeFileCompanyApproval(state, payload) {
      if (!state.file || !payload || state.file.id !== payload.id) return
      const merged = {
        ...state.file,
        company_approval: payload.company_approval,
        company_approval_reason: payload.company_approval_reason,
        company_approval_at: payload.company_approval_at,
        company_approval_by: payload.company_approval_by,
      }
      state.file = merged instanceof File ? merged : new File(merged)
    },

    updateListItemCompanyApproval(state, payload) {
      if (!payload || !Array.isArray(state.files)) return
      const idx = state.files.findIndex((f) => f && f.id === payload.id)
      if (idx === -1) return
      const merged = {
        ...state.files[idx],
        company_approval: payload.company_approval,
        company_approval_reason: payload.company_approval_reason,
        company_approval_at: payload.company_approval_at,
        company_approval_by: payload.company_approval_by,
      }
      state.files.splice(idx, 1, merged)
    },
  },

  actions: {
    async getInfoTypes({ commit }) {
      const { data } = await Api.get(`information-types`)
      commit('setInfoTypes', data.data)
    },

    async getFiles({ commit }, page) {
      const { data } = await Api.get(`file?page=${page}`)
      commit('clearFiles')
      commit('clearFilesMeta')

      if (data) {
        const fileList = Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.files)
          ? data.files
          : []
        const meta = data.meta || { current_page: 1, per_page: 10, total: 0 }
        commit('setFiles', fileList)
        commit('setFilesMeta', meta)
      }
    },

    // Accepts a plain file id, or { id, projectId } when the file is being
    // viewed in a specific project's context — company_approval and friends
    // then reflect that project's decision rather than rolling up across
    // every project this viewer's company has an opinion on for the file
    // (see FileResource::toArray()'s project_id handling).
    async getFile({ commit }, payload) {
      const id = payload && typeof payload === 'object' ? payload.id : payload
      const projectId =
        payload && typeof payload === 'object' ? payload.projectId : null
      const params = projectId != null ? { project_id: projectId } : {}
      const { data } = await Api.get(`file/${id}`, { params })
      commit('setFile', data.data)
    },

    async getSearchedFiles({ commit }, payload) {
      const name = typeof payload === 'string' ? payload : payload.search
      const page =
        typeof payload === 'object' && payload && payload.page != null
          ? payload.page
          : 1
      const limit =
        typeof payload === 'object' && payload && payload.limit != null
          ? payload.limit
          : 10

      const file = new File()
      const query = file.page(page).limit(limit).where('search', name)
      const res = await query.get()
      commit('clearFiles')
      commit('clearFilesMeta')

      if (res == null) {
        return
      }

      let fileList = []
      let meta = null

      if (Array.isArray(res)) {
        fileList = res
        meta = {
          current_page: page,
          per_page: limit,
          total: res.length,
        }
      } else if (res.data !== undefined) {
        fileList = Array.isArray(res.data) ? res.data : []
        meta =
          res.meta ||
          (fileList.length
            ? {
                current_page: page,
                per_page: limit,
                total: fileList.length,
              }
            : {
                current_page: page,
                per_page: limit,
                total: 0,
              })
      }

      commit('setFiles', fileList)
      commit(
        'setFilesMeta',
        meta || { current_page: page, per_page: limit, total: 0 },
      )
    },

    async getValidFiles({ commit }, payload) {
      // Back-compat: accept a bare name string as well as {name, templateId}.
      const { name, templateId } =
        typeof payload === 'string'
          ? { name: payload, templateId: null }
          : payload || {}

      const file = new File()
      // "valid" alone excludes files still awaiting company approval, so a
      // tradie who already uploaded a matching file can't select it here
      // while it's pending review (browse-existing-file flow on requests).
      // "expiring soon" must stay in too: FileController::getFileStatus()
      // puts any file with an expiry date within 30 days into that status
      // (not "valid" - that only comes from licence verification), so
      // without it a file drops out of this picker the moment it nears
      // expiry even though it's still the tradie's current document.
      const { data } = await file
        .where('status', 'valid,pending approval,expiring soon')
        .get()
      commit('clearFiles')

      if (data) {
        commit('setFiles', filterFilesForRequest(data, { name, templateId }))
      }
    },

    async getSharedFiles({ commit }, payload) {
      const page =
        typeof payload === 'object' && payload ? payload.page : payload
      const search =
        typeof payload === 'object' && payload ? payload.search : null
      const params = { page }
      if (search) params['filter[search]'] = search

      const { data } = await Api.get('file-receives', { params })
      commit('clearSharedFiles')
      commit('clearSharedFilesMeta')

      if (data) {
        commit('setSharedFiles', data.data)
        commit('setSharedFilesMeta', data.meta)
      }
    },

    async getSentFiles({ commit }, payload) {
      const page =
        typeof payload === 'object' && payload ? payload.page : payload
      const search =
        typeof payload === 'object' && payload ? payload.search : null
      const params = { page }
      if (search) params['filter[search]'] = search

      const { data } = await Api.get('file-shares', { params })
      commit('clearSentFiles')
      commit('clearSentFilesMeta')

      if (data) {
        commit('setSentFiles', data.data)
        commit('setSentFilesMeta', data.meta)
      }
    },
  },

  getters: {
    files(state) {
      const list = state.files || []
      const meta = state.filesMeta || {}

      // Company-scoped: show ALL BA-owned company files (not just "mine")
      if (meta.company_scoped_files) {
        return list.filter((file) => {
          const u = (file && file.user) || (file && file.file && file.file.user)
          return u && u.user_type === 'business_admin'
        })
      }

      // Non company-scoped: keep old behaviour (only "mine")
      return list.filter((file) => {
        const u = (file && file.user) || (file && file.file && file.file.user)
        return u && u.mine
      })
    },
    filesMeta(state) {
      return state.filesMeta
    },
    sharedFiles(state) {
      // Unlike `files` (the tradie/company's own files, filtered to "mine"),
      // these are files someone else owns and shared to the current user —
      // the backend (`file-receives`) already scopes this to shares where
      // `shared_to` is the current user, so no ownership filter applies here.
      return state.sharedFiles || []
    },
    sharedFilesMeta(state) {
      return state.sharedFilesMeta
    },
    sentFiles(state) {
      // Files the current user has shared out — the backend (`file-shares`)
      // already scopes this to shares where `shared_by` is the current user.
      return state.sentFiles || []
    },
    sentFilesMeta(state) {
      return state.sentFilesMeta
    },
    file(state) {
      return state.file
    },
  },
}
